import { ROTULO_PAPEL } from '@baiocchi/shared'
import {
  Alerta,
  Alternador,
  Botao,
  Caixa,
  Campo,
  Cartao,
  Entrada,
  EstadoVazio,
  Esqueleto,
  Etiqueta,
  Gaveta,
  Modal,
  Selecao,
  TabelaDados,
  useAvisos,
  type ColunaTabela,
} from '@baiocchi/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import { chaves } from '../../api/chaves'
import type {
  EntradaAtualizarEquipe,
  EntradaCriarEquipe,
  Equipe,
} from '@baiocchi/shared'
import { equipesApi } from '../../api/organizacao'
import { useSessao } from '../../autenticacao/contexto-sessao'
import { EstadoDeErro } from '../../componentes/estados-tela'
import { IconeEditar, IconeMais, IconeUsuario } from '../../componentes/icones'
import { opcoesDeReferencia, useUnidades } from '../../ganchos/usar-catalogos'
import { CabecalhoPagina } from '../../layout/layout-app'

/**
 * TELA-19 — Equipes.
 *
 * `Dias para finalizar` é o número de dias sem interação para a finalização
 * automática (FL-12). Vazio significa **"não finaliza"** — que é o valor de todas as
 * equipes no print do legado, e por isso é o padrão aqui: ligar finalização
 * automática por descuido encerraria carteira ativa.
 *
 * A composição da equipe é gerenciada pelo cadastro de cada usuário (multi-equipe da
 * TELA-10); esta tela **lista** os membros para conferência, mas não os edita — dois
 * lugares editando o mesmo vínculo produziriam sobrescrita silenciosa.
 */
export function PaginaEquipes() {
  const { pode } = useSessao()
  const avisos = useAvisos()
  const clienteConsulta = useQueryClient()

  const [emEdicao, setEmEdicao] = useState<Equipe | 'novo' | null>(null)
  const [membrosDe, setMembrosDe] = useState<Equipe | null>(null)
  const [mostrarInativas, setMostrarInativas] = useState(false)
  const [unidadeId, setUnidadeId] = useState('')

  const unidades = useUnidades()
  const podeGerenciar = pode('EQUIPE_GERENCIAR')

  const filtros = {
    limite: 200,
    ordenar: 'titulo',
    ...(unidadeId ? { unidadeId } : {}),
    ...(mostrarInativas ? {} : { ativo: 'true' }),
  }

  const consulta = useQuery({
    queryKey: chaves.organizacao.equipes(filtros),
    queryFn: ({ signal }) => equipesApi.listar(filtros, signal),
    retry: false,
  })

  const desativar = useMutation({
    mutationFn: (equipe: Equipe) => equipesApi.desativar(equipe.id),
    onSuccess: () => {
      void clienteConsulta.invalidateQueries({ queryKey: ['organizacao'] })
      avisos.sucesso('Equipe desativada')
    },
    onError: (erro) => avisos.erro('Não foi possível desativar', erro.message),
  })

  const colunas: ReadonlyArray<ColunaTabela<Equipe>> = [
    {
      chave: 'id',
      cabecalho: 'Id',
      classe: 'w-16 tabular-nums',
      celula: (equipe) => <span className="text-xs text-conteudo-fraco">{equipe.id}</span>,
    },
    {
      chave: 'titulo',
      cabecalho: 'Equipe',
      celula: (equipe) => (
        <div className="flex min-w-0 flex-col">
          <span className="truncate font-medium text-conteudo">{equipe.titulo}</span>
          <span className="text-xs text-conteudo-fraco">
            {equipe.unidade?.titulo ?? 'sem unidade'}
          </span>
        </div>
      ),
    },
    {
      chave: 'diasParaFinalizar',
      cabecalho: 'Dias para finalizar',
      alinhamento: 'centro',
      ocultarAte: 'md',
      celula: (equipe) =>
        equipe.diasParaFinalizar === null ? (
          <Etiqueta tom="neutro">Não finaliza</Etiqueta>
        ) : (
          <Etiqueta tom="atencao">{equipe.diasParaFinalizar} dias</Etiqueta>
        ),
    },
    {
      chave: 'totalMembros',
      cabecalho: 'Atendentes',
      alinhamento: 'centro',
      celula: (equipe) => (
        <Botao
          variante="fantasma"
          tamanho="sm"
          onClick={(evento) => {
            evento.stopPropagation()
            setMembrosDe(equipe)
          }}
          iconeEsquerda={<IconeUsuario className="h-4 w-4" />}
        >
          {equipe.totalMembros}
        </Botao>
      ),
    },
    {
      chave: 'ativo',
      cabecalho: 'Status',
      alinhamento: 'centro',
      classe: 'w-28',
      celula: (equipe) => (
        <Alternador
          marcado={equipe.ativo}
          desabilitado={!podeGerenciar || !equipe.ativo || desativar.isPending}
          onAlterar={() => desativar.mutate(equipe)}
          rotulo={equipe.ativo ? 'Ativa' : 'Inativa'}
          className="justify-center text-xs"
        />
      ),
    },
    {
      chave: 'acoes',
      cabecalho: '',
      alinhamento: 'direita',
      classe: 'w-16',
      celula: (equipe) => (
        <Botao
          variante="fantasma"
          tamanho="icone"
          aria-label={`Editar ${equipe.titulo}`}
          disabled={!podeGerenciar}
          onClick={(evento) => {
            evento.stopPropagation()
            setEmEdicao(equipe)
          }}
        >
          <IconeEditar className="h-4 w-4" />
        </Botao>
      ),
    },
  ]

  return (
    <>
      <CabecalhoPagina
        titulo="Equipes"
        descricao="Cada equipe pertence a uma unidade. A composição vem do cadastro de cada usuário."
        acoes={
          podeGerenciar ? (
            <Botao
              onClick={() => setEmEdicao('novo')}
              iconeEsquerda={<IconeMais className="h-4 w-4" />}
            >
              Nova equipe
            </Botao>
          ) : null
        }
      />

      <Cartao className="mb-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="min-w-52">
            <Selecao
              vazio="Todas as unidades"
              aria-label="Filtrar por unidade"
              value={unidadeId}
              opcoes={opcoesDeReferencia(unidades.data?.dados)}
              onChange={(evento) => setUnidadeId(evento.target.value)}
            />
          </div>
          <Caixa
            rotulo="Mostrar equipes desativadas"
            dica="A migração traz só as ativas e as inativas que tenham leads vinculados."
            checked={mostrarInativas}
            onChange={(evento) => setMostrarInativas(evento.target.checked)}
          />
        </div>
      </Cartao>

      <Cartao semPadding>
        <TabelaDados
          colunas={colunas}
          itens={consulta.data?.dados ?? []}
          chaveDe={(equipe) => String(equipe.id)}
          carregando={consulta.isPending}
          erro={
            consulta.isError ? (
              <EstadoDeErro
                erro={consulta.error}
                aoTentarNovamente={() => void consulta.refetch()}
              />
            ) : undefined
          }
          realce={(equipe) => !equipe.ativo}
          vazio={<EstadoVazio titulo="Nenhuma equipe cadastrada" />}
        />
      </Cartao>

      <ModalEquipe equipe={emEdicao} onFechar={() => setEmEdicao(null)} />
      <GavetaMembros equipe={membrosDe} onFechar={() => setMembrosDe(null)} />
    </>
  )
}

function ModalEquipe({
  equipe,
  onFechar,
}: {
  equipe: Equipe | 'novo' | null
  onFechar: () => void
}) {
  const avisos = useAvisos()
  const clienteConsulta = useQueryClient()
  const unidades = useUnidades()
  const ehNova = equipe === 'novo'
  const registro = equipe !== 'novo' && equipe !== null ? equipe : null

  const [estado, setEstado] = useState({
    titulo: '',
    unidadeId: '',
    finalizaAutomaticamente: false,
    diasParaFinalizar: 30,
  })
  const [erro, setErro] = useState<string | null>(null)

  // Dependência é a identidade do registro em edição, não seus campos: incluí-los
  // faria o efeito rodar a cada `refetch` e sobrescrever o que está sendo digitado.
  useEffect(() => {
    if (equipe === null) return
    setErro(null)
    setEstado({
      titulo: registro?.titulo ?? '',
      unidadeId: registro?.unidade ? String(registro.unidade.id) : '',
      finalizaAutomaticamente: (registro?.diasParaFinalizar ?? null) !== null,
      diasParaFinalizar: registro?.diasParaFinalizar ?? 30,
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [equipe])

  /**
   * `ativo` **não** entra no corpo: desativar e reativar têm rota própria, porque
   * cada uma confere vínculos e audita. O schema compartilhado é `.strict()` e
   * recusava o corpo inteiro com `422` — a equipe não era criada nem salva.
   *
   * `responsavelUuid` também não entra no `PATCH`: é `PUT /equipes/{id}/responsavel`.
   *
   * As anotações de tipo são o que faz o TypeScript recusar campo a mais — excesso de
   * propriedade só é conferido em literal anotado, não em variável solta.
   */
  const salvar = useMutation({
    mutationFn: () => {
      const diasParaFinalizar = estado.finalizaAutomaticamente
        ? Number(estado.diasParaFinalizar)
        : null

      if (ehNova) {
        const corpo: EntradaCriarEquipe = {
          titulo: estado.titulo.trim(),
          unidadeId: Number(estado.unidadeId),
          diasParaFinalizar,
        }
        return equipesApi.criar(corpo)
      }

      const corpo: EntradaAtualizarEquipe = {
        titulo: estado.titulo.trim(),
        unidadeId: Number(estado.unidadeId),
        diasParaFinalizar,
      }
      return equipesApi.atualizar(registro?.id as number, corpo)
    },
    onSuccess: () => {
      void clienteConsulta.invalidateQueries({ queryKey: ['organizacao'] })
      avisos.sucesso(ehNova ? 'Equipe criada' : 'Equipe atualizada')
      onFechar()
    },
    onError: (causa) => setErro(causa instanceof Error ? causa.message : 'Não foi possível salvar.'),
  })

  return (
    <Modal
      aberto={equipe !== null}
      onFechar={onFechar}
      travado={salvar.isPending}
      titulo={ehNova ? 'Nova equipe' : `Editar ${registro?.titulo ?? ''}`}
      largura="sm"
      rodape={
        <>
          <Botao variante="contorno" onClick={onFechar} disabled={salvar.isPending}>
            Fechar
          </Botao>
          <Botao
            onClick={() => salvar.mutate()}
            carregando={salvar.isPending}
            disabled={estado.titulo.trim().length < 2 || !estado.unidadeId}
          >
            Salvar
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {erro ? <Alerta tom="perigo">{erro}</Alerta> : null}

        <Campo rotulo="Título" obrigatorio>
          {(atributos) => (
            <Entrada
              {...atributos}
              autoFocus
              value={estado.titulo}
              onChange={(evento) => setEstado((atual) => ({ ...atual, titulo: evento.target.value }))}
            />
          )}
        </Campo>

        <Campo
          rotulo="Unidade"
          obrigatorio
          dica="Um lead só pode ter equipe da própria unidade."
        >
          {(atributos) => (
            <Selecao
              {...atributos}
              vazio="Selecione a unidade"
              value={estado.unidadeId}
              opcoes={opcoesDeReferencia(unidades.data?.dados)}
              onChange={(evento) =>
                setEstado((atual) => ({ ...atual, unidadeId: evento.target.value }))
              }
            />
          )}
        </Campo>

        <Caixa
          rotulo="Finalizar leads inativos automaticamente"
          dica="Desmarcado, nenhum lead desta equipe é encerrado pelo sistema."
          checked={estado.finalizaAutomaticamente}
          onChange={(evento) =>
            setEstado((atual) => ({ ...atual, finalizaAutomaticamente: evento.target.checked }))
          }
        />

        {estado.finalizaAutomaticamente && (
          <Campo
            rotulo="Dias sem interação para finalizar"
            dica="Contado da última interação registrada no lead."
          >
            {(atributos) => (
              <Entrada
                {...atributos}
                type="number"
                min={1}
                value={String(estado.diasParaFinalizar)}
                onChange={(evento) =>
                  setEstado((atual) => ({
                    ...atual,
                    diasParaFinalizar: Number(evento.target.value),
                  }))
                }
              />
            )}
          </Campo>
        )}

        {/*
          Sem caixa "Ativa" aqui: o status muda pelo alternador da coluna Status,
          que chama `desativar`/`reativar` — as rotas que conferem vínculos e auditam.
        */}
      </div>
    </Modal>
  )
}

function GavetaMembros({ equipe, onFechar }: { equipe: Equipe | null; onFechar: () => void }) {
  const consulta = useQuery({
    queryKey: chaves.organizacao.membros(equipe?.id ?? 0),
    queryFn: ({ signal }) => equipesApi.membros(equipe?.id as number, signal),
    enabled: equipe !== null,
    retry: false,
  })

  if (!equipe) return null

  return (
    <Gaveta
      aberta
      onFechar={onFechar}
      titulo={equipe.titulo}
      descricao="Membros da equipe. Para alterar, edite o cadastro do usuário."
    >
      {consulta.isPending ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }, (_, indice) => (
            <Esqueleto key={indice} className="h-12 w-full" />
          ))}
        </div>
      ) : consulta.isError ? (
        <EstadoDeErro
          erro={consulta.error}
          aoTentarNovamente={() => void consulta.refetch()}
        />
      ) : (consulta.data?.dados.length ?? 0) === 0 ? (
        <EstadoVazio
          titulo="Equipe sem membros"
          descricao="Uma equipe sem atendentes ativos não recebe distribuição de leads."
        />
      ) : (
        <ul className="flex flex-col divide-y divide-borda">
          {consulta.data?.dados.map((membro) => (
            <li key={membro.uuid} className="flex items-center justify-between gap-3 py-3">
              <div className="min-w-0">
                <Link
                  to={`/usuarios/${membro.uuid}`}
                  className="block truncate font-medium text-marca-600 hover:underline"
                >
                  {membro.nome}
                </Link>
                <span className="text-xs text-conteudo-fraco">{ROTULO_PAPEL[membro.papel]}</span>
              </div>
              <div className="flex shrink-0 gap-1">
                {membro.receberLeads ? <Etiqueta tom="sucesso">Recebe leads</Etiqueta> : null}
                {!membro.ativo ? <Etiqueta tom="perigo">Desativado</Etiqueta> : null}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Gaveta>
  )
}
